import { ProcedureStatus, DbRef, InstructionType } from '@caresend/types';
import { hasStatus, getProcedurePath, objectFilter, objectMap, hashProcedureForSupplyConfig, isNullish, formatAddressUS, formatFullName, formatPhoneNumber, getFormattedInstructions, buildBulletSeparatedString, nullishFilter, isProductWithTubeConfig, isProductWithPCRConfig, isProductWithUrineConfig } from '@caresend/utils';
import update from 'immutability-helper';
import 'firebase/auth';
import { cancelProcedureRequest } from '../../../database/firebaseConfig/cloudFunctions.js';
import '../../../database/firebaseConfig/init.js';
import { getValueOnce, dbGroupSet } from '../../../database/firebaseConfig/methods.js';
import '@sentry/vue';
import { toastErrorAndReport, toastInfo, toastSuccess, toastError } from '../../../functions/toast/toast.js';
import '@fortawesome/fontawesome-svg-core';
import 'buefy';
import '../../../components/ui/Icon.vue.js';
import '../../../functions/configuration/icons.js';
import '../../../database/algoliaConfig/indices.js';
import 'mobile-device-detect';
import '../../../functions/tracking/index.js';
import '../../../router/helpers/initRouter.js';
import '@firebase/auth';
import 'browser-image-compression';
import 'firebase/storage';
import 'js-sha256';
import { initExtendedModule } from '../helpers.js';
import { sampleExists, getOrderProcedures, getProcedureDateTimeInfo } from './helpers.js';
import { SetOrUnset } from './model.js';

/* eslint-disable max-len */
const proceduresState = {
    procedures: {},
};
const proceduresMutations = {
    'procedures/SET_PROCEDURE_SPECIMEN_BAG': (state, { procedureID, newSpecimenBag }) => {
        state.procedures = update(state.procedures, {
            [procedureID]: {
                specimenBag: { $set: newSpecimenBag },
            },
        });
    },
    'procedures/DELETE_PROCEDURE': (state, procedureID) => {
        var _a;
        state.procedures = update((_a = state.procedures) !== null && _a !== void 0 ? _a : {}, {
            $unset: [procedureID],
        });
    },
    'procedures/UNSET_PROCEDURE_SAMPLE_AMBER_BAG': (state, { procedureID, sampleID, amberBagID, }) => {
        var _a, _b;
        const currentProcedure = state.procedures[procedureID];
        if (!currentProcedure)
            return;
        const currentSample = (_b = (_a = state.procedures[procedureID]) === null || _a === void 0 ? void 0 : _a.samples) === null || _b === void 0 ? void 0 : _b[sampleID];
        if (!currentSample)
            return;
        const currentAmberBags = currentProcedure.amberBags;
        currentAmberBags === null || currentAmberBags === void 0 ? true : delete currentAmberBags[amberBagID];
        state.procedures = {
            ...state.procedures,
            [procedureID]: {
                ...currentProcedure,
                amberBags: currentAmberBags,
            },
        };
    },
    'procedures/UPDATE_PROCEDURE': (state, procedureUpdate) => {
        const procedure = state.procedures[procedureUpdate.id];
        if (!procedure) {
            toastErrorAndReport(`Missing procedure ${procedureUpdate.id}`);
            return;
        }
        const newProcedure = {
            ...procedure,
            ...procedureUpdate,
        };
        state.procedures = {
            ...state.procedures,
            [newProcedure.id]: newProcedure,
        };
    },
    'procedures/SET_PROCEDURE_SAMPLE_STATUS': (state, { procedureID, sampleID, status, collectionFailure, dropOffFailure, }) => {
        if (!sampleExists(state, procedureID, sampleID))
            return;
        state.procedures = update(state.procedures, {
            [procedureID]: {
                samples: {
                    [sampleID]: {
                        status: { $set: status },
                        collectionFailure: { $set: collectionFailure },
                        dropOffFailure: { $set: dropOffFailure },
                    },
                },
            },
        });
    },
    'procedures/SET_PROCEDURE_INSULATED_BAG': (state, { procedureID, sampleID, insulatedBagSupplyItemID, insulatedBagSupplyID, previousSupplyItem, }) => {
        var _a, _b, _c;
        const currentProcedure = state.procedures[procedureID];
        if (!currentProcedure || !insulatedBagSupplyItemID)
            return;
        const currentSample = (_b = (_a = state.procedures[procedureID]) === null || _a === void 0 ? void 0 : _a.samples) === null || _b === void 0 ? void 0 : _b[sampleID];
        if (!currentSample)
            return;
        const currentInsulatedBagSupplyItems = (_c = currentProcedure.insulatedBag) === null || _c === void 0 ? void 0 : _c.relatedSupplyItems;
        state.procedures = {
            ...state.procedures,
            [procedureID]: {
                ...currentProcedure,
                insulatedBag: {
                    id: insulatedBagSupplyItemID,
                    supplyID: insulatedBagSupplyID,
                    relatedSupplyItems: {
                        ...currentInsulatedBagSupplyItems,
                        [previousSupplyItem.supplyItem.id]: previousSupplyItem.supplyItem,
                    },
                },
            },
        };
    },
    'procedures/SET_PROCEDURE_SPECIMEN_BAG_ID': (state, { instruction, procedureID, specimenBagSupplyItemID, specimenBagSupplyID, }) => {
        var _a;
        const currentProcedure = state.procedures[procedureID];
        if (!currentProcedure) {
            toastErrorAndReport('Missing procedure when setting specimen bag ID.');
            return;
        }
        let newSpecimenBag = update((_a = currentProcedure.specimenBag) !== null && _a !== void 0 ? _a : {}, {
            supplyID: { $set: specimenBagSupplyID },
        });
        if (instruction === SetOrUnset.SET) {
            newSpecimenBag = update(newSpecimenBag, {
                id: { $set: specimenBagSupplyItemID },
            });
        }
        else {
            newSpecimenBag = update(newSpecimenBag, { $unset: ['id'] });
        }
        state.procedures = update(state.procedures, {
            [procedureID]: {
                specimenBag: { $set: newSpecimenBag },
            },
        });
    },
    'procedures/SET_PROCEDURE_KIT_ID': (state, { instruction, procedureID, procedureKitSupplyItemID, procedureKitSupplyID, }) => {
        const currentProcedure = state.procedures[procedureID];
        if (!currentProcedure) {
            toastErrorAndReport('Missing procedure when setting procedure kit ID.');
            return;
        }
        const set = instruction === SetOrUnset.SET;
        let newProcedure;
        if (set) {
            const newKitSupplyItem = {
                id: procedureKitSupplyItemID,
                supplyID: procedureKitSupplyID,
            };
            newProcedure = update(currentProcedure, {
                kitSupplyItem: { $set: newKitSupplyItem },
            });
        }
        else {
            newProcedure = update(currentProcedure, { $unset: ['kitSupplyItem'] });
        }
        state.procedures = update(state.procedures, {
            [procedureID]: { $set: newProcedure },
        });
    },
    'procedures/SET_PROCEDURE_SPECIMEN_BAG_SUPPLY_ITEM': (state, { procedureID, specimenBagSupplyItem, }) => {
        var _a, _b, _c;
        const currentProcedure = state.procedures[procedureID];
        if (!currentProcedure || !specimenBagSupplyItem)
            return;
        /**
         * Due to the charting flow, samples are scanned prior to the specimen bag.
         * This means that the specimen bag supply item may not exist in the procedure
         */
        state.procedures = update(state.procedures, {
            [procedureID]: { $set: update(currentProcedure, {
                    specimenBag: { $set: update((_a = currentProcedure.specimenBag) !== null && _a !== void 0 ? _a : {}, {
                            relatedSupplyItems: { $set: update((_c = (_b = currentProcedure.specimenBag) === null || _b === void 0 ? void 0 : _b.relatedSupplyItems) !== null && _c !== void 0 ? _c : {}, {
                                    [specimenBagSupplyItem.id]: { $set: specimenBagSupplyItem },
                                }) },
                        }) },
                }) },
        });
    },
    'procedures/SET_PROCEDURE': (state, procedure) => {
        var _a, _b;
        const procedureID = procedure.id;
        /**
         * Prevent individual procedure binds overwriting previously fetched
         * friendly status
         */
        const friendlyStatus = (_a = state.procedures[procedure.id]) === null || _a === void 0 ? void 0 : _a.friendlyStatus;
        const procedureWithFriendlyStatus = {
            ...procedure,
            friendlyStatus,
        };
        state.procedures = update((_b = state.procedures) !== null && _b !== void 0 ? _b : {}, {
            [procedureID]: { $set: procedureWithFriendlyStatus },
        });
    },
    /** Used by setOrderDataInStore */
    'procedures/SET_PROCEDURES': (state, procedures) => {
        var _a;
        state.procedures = update((_a = state.procedures) !== null && _a !== void 0 ? _a : {}, {
            $merge: procedures,
        });
    },
    'procedures/UPDATE_DOCUMENTS': (state, { procedureID, documents, }) => {
        var _a;
        state.procedures = update((_a = state.procedures) !== null && _a !== void 0 ? _a : {}, {
            [procedureID]: {
                documents: { $set: documents },
            },
        });
    },
    'procedures/SET_PROCEDURE_SAMPLE_SUPPLY_ITEM': (state, { procedureID, sampleID, supplyItemID, }) => {
        if (!sampleExists(state, procedureID, sampleID))
            return;
        state.procedures = update(state.procedures, {
            [procedureID]: {
                samples: {
                    [sampleID]: {
                        supplyItemID: { $set: supplyItemID },
                    },
                },
            },
        });
    },
    'procedures/SET_PROCEDURE_SAMPLE_AMBER_BAG': (state, { procedureID, sampleID, amberBag, }) => {
        var _a, _b;
        const currentProcedure = state.procedures[procedureID];
        if (!currentProcedure || !amberBag)
            return;
        const currentSample = (_b = (_a = state.procedures[procedureID]) === null || _a === void 0 ? void 0 : _a.samples) === null || _b === void 0 ? void 0 : _b[sampleID];
        if (!currentSample)
            return;
        state.procedures = {
            ...state.procedures,
            [procedureID]: {
                ...currentProcedure,
                amberBags: {
                    ...currentProcedure.amberBags,
                    [amberBag.id]: amberBag,
                },
            },
        };
    },
};
const proceduresActions = {
    'procedures/cancelProcedure': async ({ state }, procedureID) => {
        try {
            const procedure = state.procedures[procedureID];
            if (!procedure)
                throw Error('Procedure not found');
            const isCanceled = hasStatus(procedure, ProcedureStatus.CANCELED);
            if (isCanceled) {
                toastInfo('Procedure already canceled');
                return;
            }
            await cancelProcedureRequest({
                procedureID,
            });
            toastSuccess('Procedure canceled');
        }
        catch (error) {
            toastError(error);
        }
    },
    'procedures/fetchProcedure': async (_, procedureID) => {
        const encryptedProcedure = await getValueOnce(getProcedurePath(procedureID));
        return encryptedProcedure;
    },
    'procedures/saveProcedureSupplyConfiguration': async ({ state, rootState }, { procedureID, inputSupplyInstructionConfig, }) => {
        var _a, _b, _c;
        // Clean up supplies with 0 quantity
        let supplyInstructions = objectFilter(inputSupplyInstructionConfig.supplyInstructions, (s) => s.quantity !== 0);
        // Ensure subSupplies is not passed as undefined to firebase
        supplyInstructions = objectMap(supplyInstructions, (s) => ({
            ...s,
        }));
        const usageInstructions = inputSupplyInstructionConfig.usageInstructions || null;
        const supplyInstructionsUpdated = {
            updatedAt: Date.now(),
            updatedBy: (_b = (_a = rootState.auth.user) === null || _a === void 0 ? void 0 : _a.id) !== null && _b !== void 0 ? _b : 'Unknown user',
        };
        const supplyInstructionConfiguration = {
            ...inputSupplyInstructionConfig,
            supplyInstructions,
            supplyInstructionsUpdated,
            usageInstructions,
        };
        const procedurePath = `${DbRef.PROCEDURES}/${procedureID}`;
        const procedure = state.procedures[procedureID];
        if (!procedure)
            return;
        const procedureHash = await hashProcedureForSupplyConfig(procedure);
        const configPath = `${DbRef.SUPPLY_CONFIGURATIONS}/${procedureHash}`;
        const procedureSupplyPath = `${procedurePath}/supplyInstructionConfig`;
        const procedureQAPath = `${procedurePath}/qualityControl/intake`;
        const updates = {
            [procedureSupplyPath]: supplyInstructionConfiguration,
            [procedureQAPath]: true,
            [configPath]: supplyInstructionConfiguration,
        };
        const aSampleRequiresProcessing = !!Object.keys((_c = inputSupplyInstructionConfig.processingIncludes) !== null && _c !== void 0 ? _c : {}).length;
        const disableSampleProcessingDataExists = !isNullish(procedure.disableSampleProcessing);
        if (!aSampleRequiresProcessing && disableSampleProcessingDataExists) {
            updates[`${procedurePath}/disableSampleProcessing`] = null;
        }
        const { orderID } = procedure;
        const order = rootState.orders.orders[orderID !== null && orderID !== void 0 ? orderID : ''];
        if (order && orderID) {
            // Workaround to avoid dependency cycle.
            const orderProcedures = getOrderProcedures(orderID, rootState);
            const isOrderQaComplete = !Object.values(orderProcedures)
                .filter((p) => p.id !== procedureID)
                .some((p) => { var _a; return !((_a = p.qualityControl) === null || _a === void 0 ? void 0 : _a.intake); });
            if (isOrderQaComplete) {
                const bookingQAPath = `${DbRef.ORDERS}/${orderID}/qualityControl/intake`;
                updates[bookingQAPath] = true;
            }
        }
        await dbGroupSet(updates);
    },
};
const proceduresGetters = {
    'procedures/getSamplesRequiringCentrifugation': (state) => (procedureID) => proceduresGetters['procedures/getSamplesByProcedureID'](state)(procedureID).filter(({ processing }) => !!processing.centrifuge),
    'procedures/getPrimaryContactPhoneNumber': (state, _getters, rootState) => (waypointID) => {
        var _a, _b;
        const waypoint = rootState.waypoint.waypoints[waypointID];
        if (!waypoint || !waypoint.waypointActions)
            return;
        const firstWaypointActionIDObj = waypoint.waypointActions[0];
        if (!firstWaypointActionIDObj)
            return;
        const firstWaypointActionID = firstWaypointActionIDObj.id;
        const firstWaypointAction = rootState.waypoint.waypointActions[firstWaypointActionID];
        if (!firstWaypointAction || !firstWaypointAction.procedures)
            return;
        const procedureKeys = Object.keys(firstWaypointAction.procedures);
        if (!procedureKeys.length)
            return;
        const firstProcedureID = procedureKeys[0];
        if (!firstProcedureID)
            return;
        const firstProcedure = state.procedures[firstProcedureID];
        const contact = firstProcedure === null || firstProcedure === void 0 ? void 0 : firstProcedure.contact;
        if (!contact)
            return;
        if (contact.custom) {
            if (!contact.phone)
                console.error('This is a custom primary contact but no phone number was found.');
            return contact.phone;
        }
        const { userID } = contact;
        const user = (_a = rootState.users.users) === null || _a === void 0 ? void 0 : _a[userID !== null && userID !== void 0 ? userID : ''];
        if (!user) {
            console.error('The primary contact is a patient but they were not found.');
            return;
        }
        return (_b = user.info) === null || _b === void 0 ? void 0 : _b.phone;
    },
    'procedures/getSamplesByProcedureID': (state) => (procedureID) => {
        var _a;
        const procedure = state.procedures[procedureID];
        return Object.values((_a = procedure === null || procedure === void 0 ? void 0 : procedure.samples) !== null && _a !== void 0 ? _a : {});
    },
    'procedures/getFormattedAddress': (state) => (procedureID) => {
        const procedure = state.procedures[procedureID];
        if (!procedure || !procedure.location)
            return;
        return formatAddressUS(procedure.location);
    },
    'procedures/getFormattedContactInfo': (state, _getters, rootState) => (procedureID) => {
        var _a, _b, _c;
        const procedure = state.procedures[procedureID];
        if (!procedure || !procedure.contact)
            return;
        if (procedure.contact.userID) {
            const { userID } = procedure.contact;
            /** the contact could be a staff member so we should check that as well */
            const user = (_b = (_a = rootState.users.users) === null || _a === void 0 ? void 0 : _a[userID]) !== null && _b !== void 0 ? _b : (_c = rootState.users.staffUsers) === null || _c === void 0 ? void 0 : _c[userID];
            const userFullName = formatFullName(user);
            if (!(user === null || user === void 0 ? void 0 : user.info))
                return userFullName;
            const { phone } = user === null || user === void 0 ? void 0 : user.info;
            const formattedPhone = formatPhoneNumber(phone);
            return `${userFullName} - ${formattedPhone}`;
        }
        return `${procedure.contact.name} - ${formatPhoneNumber(procedure.contact.phone)}`;
    },
    'procedures/getFormattedInstructions': (state) => (procedureID, instructionType = InstructionType.NURSE) => {
        const procedure = state.procedures[procedureID];
        if (!procedure)
            return;
        return getFormattedInstructions(procedure, instructionType);
    },
    'procedures/getFormattedLabInformation': (state, _getters, rootState, rootGetters) => (procedureID) => {
        var _a, _b, _c, _d;
        const infoItems = [];
        const addPlaceAccountToList = ({ id }) => {
            var _a;
            const placeAccount = (_a = rootState.places.placeAccounts) === null || _a === void 0 ? void 0 : _a[id];
            if (!placeAccount)
                return;
            let accountDetails = `${placeAccount.name}`;
            if (placeAccount.account)
                accountDetails = `${accountDetails} (${placeAccount.account})`;
            if (placeAccount.noAccount)
                accountDetails = 'No account';
            const placeGroup = rootGetters['places/getPlaceGroupByPlaceAccount'](placeAccount);
            if (!placeGroup)
                return accountDetails;
            infoItems.push(`${placeGroup.name}: ${accountDetails}`);
        };
        if (procedureID && ((_b = (_a = state.procedures) === null || _a === void 0 ? void 0 : _a[procedureID]) === null || _b === void 0 ? void 0 : _b.placeAccounts)) {
            objectMap((_d = (_c = state.procedures[procedureID]) === null || _c === void 0 ? void 0 : _c.placeAccounts) !== null && _d !== void 0 ? _d : {}, addPlaceAccountToList);
            return buildBulletSeparatedString(infoItems);
        }
        return buildBulletSeparatedString(infoItems);
    },
    'procedures/getFormattedProcedureDateTime': (state) => (procedureID) => {
        const procedure = state.procedures[procedureID];
        if (!procedure || !procedure.dateInfo)
            return;
        return getProcedureDateTimeInfo(procedure);
    },
    'procedures/getPatientName': (state, _getters, rootState, rootGetters) => (procedureID) => {
        var _a, _b;
        const procedure = state.procedures[procedureID];
        if (!procedure)
            return 'Unknown patient';
        const booking = (_b = (_a = rootState.bookings) === null || _a === void 0 ? void 0 : _a.bookings) === null || _b === void 0 ? void 0 : _b[procedure.bookingID];
        if (!booking)
            return 'Unknown patient';
        const { patientID } = booking;
        const name = rootGetters['users/getUserFullName'](patientID);
        return name;
    },
    'procedures/getPatientOnProcedure': (state, _getters, rootState) => (
    /** Procedure object or ID */
    procedure) => {
        var _a, _b, _c, _d;
        const procedureObj = typeof procedure === 'string'
            ? (_a = state.procedures) === null || _a === void 0 ? void 0 : _a[procedure]
            : procedure;
        const { bookingID } = procedureObj !== null && procedureObj !== void 0 ? procedureObj : {};
        if (!bookingID)
            return null;
        const booking = (_b = rootState.bookings.bookings[bookingID]) !== null && _b !== void 0 ? _b : null;
        const patientID = booking === null || booking === void 0 ? void 0 : booking.patientID;
        if (!patientID)
            return null;
        return (_d = (_c = rootState.users.users) === null || _c === void 0 ? void 0 : _c[patientID]) !== null && _d !== void 0 ? _d : null;
    },
    'procedures/getProcedureByID': (state) => (procedureID) => {
        if (!procedureID)
            return;
        return state.procedures[procedureID];
    },
    'procedures/getProcedureFlowTypeByProcedureID': (state, _getters, _rootState, rootGetters) => (procedureID) => {
        if (isNullish(procedureID))
            return undefined;
        const procedure = state.procedures[procedureID];
        if (isNullish(procedure) || isNullish(procedure.taskID))
            return undefined;
        const task = rootGetters['variables/getTaskByID'](procedure.taskID);
        return task === null || task === void 0 ? void 0 : task.info.procedureFlowType;
    },
    'procedures/getProcedureProductInfo': (state, _getters, rootState) => (procedureID) => {
        var _a, _b, _c;
        if (!procedureID)
            return undefined;
        const procedure = state.procedures[procedureID];
        if (isNullish(procedure))
            return;
        const { taskID, products } = procedure;
        const productInfos = Object.keys(products !== null && products !== void 0 ? products : {})
            .map((id) => { var _a, _b, _c; return (_c = (_b = (_a = rootState.variables.variables) === null || _a === void 0 ? void 0 : _a.products) === null || _b === void 0 ? void 0 : _b[id]) === null || _c === void 0 ? void 0 : _c.info; })
            .filter(nullishFilter);
        const taskInfo = (_c = (_b = (_a = rootState.variables.variables) === null || _a === void 0 ? void 0 : _a.tasks) === null || _b === void 0 ? void 0 : _b[taskID]) === null || _c === void 0 ? void 0 : _c.info;
        if (productInfos.length === 1 && productInfos[0]) {
            /**
             * Joining task and product info allows for more specific names than task info
             * and provides a fallback for missing information at the product info level.
             * i.e. task.info has a picture source, product.info might not
             */
            const joinedInfo = {
                ...taskInfo,
                ...productInfos[0],
            };
            return joinedInfo;
        }
        return taskInfo;
    },
    'procedures/getProcedureTimeZone': (state) => (procedureID) => {
        var _a, _b, _c, _d;
        const procedure = state.procedures[procedureID];
        return (_d = (_c = (_b = (_a = procedure === null || procedure === void 0 ? void 0 : procedure.dateInfo) === null || _a === void 0 ? void 0 : _a[0]) === null || _b === void 0 ? void 0 : _b.dateInstruction) === null || _c === void 0 ? void 0 : _c.timeZone) !== null && _d !== void 0 ? _d : null;
    },
    'procedures/getProcedurePlaceGroupConfig': (state, _getters, rootState) => (procedure) => {
        var _a, _b, _c;
        const procedureObj = typeof procedure === 'string'
            ? (_a = state.procedures) === null || _a === void 0 ? void 0 : _a[procedure]
            : procedure;
        if (!procedureObj)
            return;
        const dropOffPlaceGroupID = (_c = Object.values((_b = procedureObj.dropOffPlaceGroups) !== null && _b !== void 0 ? _b : {})[0]) === null || _c === void 0 ? void 0 : _c.id;
        if (!dropOffPlaceGroupID)
            return;
        const { products } = procedureObj;
        const configsWithContext = objectFilter(objectMap(products, ({ id }) => {
            var _a, _b, _c, _d, _e;
            const product = (_b = (_a = rootState.variables.variables) === null || _a === void 0 ? void 0 : _a.products) === null || _b === void 0 ? void 0 : _b[id];
            if (!product)
                return;
            if (isProductWithTubeConfig(product) || isProductWithPCRConfig(product)) {
                const placeGroupConfig = (_c = product.placeGroups) === null || _c === void 0 ? void 0 : _c[dropOffPlaceGroupID];
                if (!placeGroupConfig)
                    return;
                const { supplyID } = placeGroupConfig;
                const supply = (_d = rootState.variables.variables) === null || _d === void 0 ? void 0 : _d.supplies[supplyID];
                if (!supply)
                    return;
                return {
                    product,
                    placeGroupConfig,
                    supply,
                };
            }
            if (isProductWithUrineConfig(product)) {
                const placeGroupConfig = (_e = product.placeGroups) === null || _e === void 0 ? void 0 : _e[dropOffPlaceGroupID];
                if (!placeGroupConfig)
                    return;
                const { specimen } = placeGroupConfig;
                return specimen.map(({ supplyID }) => {
                    var _a;
                    const supply = (_a = rootState.variables.variables) === null || _a === void 0 ? void 0 : _a.supplies[supplyID];
                    if (!supply)
                        return;
                    return {
                        product,
                        placeGroupConfig,
                        supply,
                    };
                }).filter(nullishFilter);
            }
        }), nullishFilter);
        return Object.values(configsWithContext).flat();
    },
    'procedures/getProcedureOrderID': (state, _getters, rootState) => (procedureID) => {
        var _a, _b;
        const procedure = state.procedures[procedureID];
        const bookingID = procedure === null || procedure === void 0 ? void 0 : procedure.bookingID;
        if (!bookingID)
            return;
        const orderID = (_b = (_a = rootState.bookings.bookings) === null || _a === void 0 ? void 0 : _a[bookingID]) === null || _b === void 0 ? void 0 : _b.orderID;
        return orderID;
    },
    'procedures/getProcedureOrder': (state, getters, rootState) => (procedureID) => {
        var _a;
        const orderID = proceduresGetters['procedures/getProcedureOrderID'](state, getters, rootState)(procedureID);
        if (!orderID)
            return;
        const order = (_a = rootState.orders.orders) === null || _a === void 0 ? void 0 : _a[orderID];
        return order;
    },
    'procedures/getProcedureBooking': (state, _getters, rootState) => (procedureID) => {
        var _a;
        const bookingID = (_a = state.procedures[procedureID]) === null || _a === void 0 ? void 0 : _a.bookingID;
        if (!bookingID)
            return;
        return rootState.bookings.bookings[bookingID];
    },
    'procedures/getProcedurePatientID': (state, getters, rootState) => (procedureID) => {
        const booking = proceduresGetters['procedures/getProcedureBooking'](state, getters, rootState)(procedureID);
        return booking === null || booking === void 0 ? void 0 : booking.patientID;
    },
    'procedures/getProcedureSupplyConfigString': (state, _getters, _rootState, rootGetters) => (procedureID) => {
        var _a;
        const procedure = state.procedures[procedureID];
        if (!(procedure === null || procedure === void 0 ? void 0 : procedure.supplyInstructionConfig))
            return;
        const supplyGetter = rootGetters['variables/getSupplyByID'];
        const supplies = Object.values((_a = procedure.supplyInstructionConfig.supplyInstructions) !== null && _a !== void 0 ? _a : {})
            .map((instruction) => {
            var _a, _b;
            return ({
                quantity: instruction.quantity,
                name: ((_b = (_a = supplyGetter(instruction.supplyID)) === null || _a === void 0 ? void 0 : _a.name) !== null && _b !== void 0 ? _b : '').replace('CareSend ', ''),
            });
        })
            .filter(({ name }) => !!name)
            .map(({ quantity, name }) => `${quantity} ${name}`);
        return buildBulletSeparatedString(supplies) || 'No supplies';
    },
};
const proceduresModuleExtension = {
    state: proceduresState,
    mutations: proceduresMutations,
    actions: proceduresActions,
    getters: proceduresGetters,
};
const initProceduresModule = (moduleExtensions) => initExtendedModule(proceduresModuleExtension, moduleExtensions);

export { initProceduresModule };
